// Shared store logic: catalogue helpers, cart, pricing, free-shipping progress, size finder.
// Pages render the UI; this file holds the rules. Works in the browser (window.Store) and in Node (module.exports) for tests.
(function (root) {
  const DATA = root.ALTIERO || { products: [], freeShippingThreshold: 1999 };
  const CART_KEY = 'altiero-cart';

  // Jacket size chart (kg upper bounds per size), from the brand's own chart.
  const SIZE_BY_WEIGHT = [['S', 65], ['M', 75], ['L', 85], ['XL', 95]];

  const products = DATA.products;
  const byHandle = h => products.find(p => p.handle === h);
  const siblings = p => products.filter(x => x.family === p.family);
  const inCategory = c => products.filter(p => p.category === c);
  const money = n => `${Math.round(n).toLocaleString('en-US')} EGP`;

  // Honest stock line from real availability only, in the owner's words (Theme settings > Product cards, via UI.copy)
  const say = (k, d, b, v) => String((typeof window !== 'undefined' && window.UI && window.UI.copy[k]) || d).split('[' + b + ']').join(v);
  function stockNote(p) {
    const open = p.sizes.filter(s => s.available).map(s => s.size);
    const out = p.sizes.filter(s => !s.available).map(s => s.size);
    if (!open.length) return say('stock_sold_out', 'Sold out');
    if (open.length === 1 && p.sizes.length > 1) return say('stock_only', 'Only [size] left', 'size', open[0]);
    if (out.length) return say('stock_sold_out_in', 'Sold out in [sizes]', 'sizes', out.join(', '));
    return '';
  }

  // the jacket chart's sizes and top weights: Theme settings > Size charts once ui.js has run (UI.sizeCharts), else ours
  function weights() {
    const c = typeof window !== 'undefined' && window.UI && window.UI.sizeCharts && window.UI.sizeCharts.jackets;
    const w = c ? c.rows.map(r => [r[0].split(' ')[0], window.UI.topKg(r)]).filter(([, max]) => max > 0) : [];
    return w.length ? w : SIZE_BY_WEIGHT;
  }
  function sizeForWeight(kg, roomier = false) {
    if (!(kg > 0)) return null;
    const W = weights(), top = W[W.length - 1][1];
    let i = W.findIndex(([, max]) => kg < max || (max === top && kg <= top));
    if (i === -1) return { size: W[W.length - 1][0], note: 'Above our chart. XL may be snug; message us on WhatsApp before you order.' };
    if (roomier && i < W.length - 1) i += 1;
    return { size: W[i][0], note: roomier ? 'One size up for a roomier fit.' : 'Based on our size chart.' };
  }

  // The sign-up code's percent once the shopper has joined (ui.js): Shopify counts the free-shipping minimum at discounted
  // prices, so free shipping is judged on the total after the code.
  let codePct = 0;
  const setCode = pct => { codePct = pct > 0 && pct < 100 ? +pct : 0; };

  // Pricing for any list of lines [{handle, size, qty}].
  function price(lines) {
    const items = lines.map(l => ({ ...l, product: byHandle(l.handle) })).filter(l => l.product);
    // a size priced on its own in the store (THEME.merge sets sizes[].price) counts at that price, as checkout charges it
    items.forEach(l => { const z = l.product.sizes.find(s => s.size === l.size); l.price = (z && z.price) || l.product.price; });
    // no outfit saving: the store's only discounts are the sign-up code and free shipping, so the total is the subtotal
    const total = items.reduce((s, l) => s + l.price * l.qty, 0);
    // only the three outfit categories count (a store-only piece can be in another, e.g. 'other')
    const categories = new Set(items.map(l => l.product.category).filter(c => ['jackets', 'pants', 'knitwear'].includes(c)));
    // the bag total that is still at least the threshold after the code (1,999 with 5% off: 2,105)
    const threshold = codePct ? Math.ceil(DATA.freeShippingThreshold / (1 - codePct / 100)) : DATA.freeShippingThreshold;
    return {
      items, subtotal: total, total,
      freeShipping: total >= threshold,
      toFreeShipping: Math.max(0, threshold - total),
      progress: Math.min(1, total / threshold),
      missingCategories: ['jackets', 'pants', 'knitwear'].filter(c => !categories.has(c)),
      count: items.reduce((s, l) => s + l.qty, 0),
    };
  }

  // Cart persisted in localStorage; every change fires a 'cart:change' event with the new pricing.
  const storage = {
    get() { try { return JSON.parse(root.localStorage.getItem(CART_KEY)) || []; } catch { return []; } },
    set(lines) { try { root.localStorage.setItem(CART_KEY, JSON.stringify(lines)); } catch {} },
  };
  // In the Shopify theme, THEME.cart is the real Shopify cart at page load, and THEME.sync mirrors every change to it.
  const THEME = root.THEME;
  // Only lines for a size the catalogue knows: sizes are drawn as HTML.
  const known = ls => (Array.isArray(ls) ? ls : []).filter(l => { const p = byHandle(l.handle); return p && p.sizes.some(s => s.size === l.size); });
  let lines = known(THEME ? THEME.cart : root.localStorage ? storage.get() : []);
  function commit(change) {
    storage.set(lines);
    if (THEME) THEME.sync(change, byHandle);
    const detail = { lines, pricing: price(lines), change };
    if (root.dispatchEvent && root.CustomEvent) root.dispatchEvent(new CustomEvent('cart:change', { detail }));
    return detail;
  }
  const cart = {
    lines: () => lines,
    pricing: () => price(lines),
    add(handle, size, qty = 1) {
      const p = byHandle(handle);
      if (!p || !p.sizes.some(s => s.size === size && s.available)) throw new Error(`${handle} ${size} is not available`);
      const hit = lines.find(l => l.handle === handle && l.size === size);
      if (hit) hit.qty += qty; else lines.push({ handle, size, qty });
      return commit({ type: 'add', handle, size, qty });
    },
    setQty(handle, size, qty) {
      lines = lines.map(l => (l.handle === handle && l.size === size ? { ...l, qty } : l)).filter(l => l.qty > 0);
      return commit({ type: 'qty', handle, size, qty });
    },
    remove(handle, size) { return cart.setQty(handle, size, 0); },
    clear() { lines = []; return commit({ type: 'clear' }); },
    // the theme puts the real Shopify cart back after Shopify refuses a change
    reset(next) { lines = known(next); return commit({ type: 'reset' }); },
  };

  // Suggested add-ons for the bag: up to two in-stock pieces from each missing category: the owner's Frequently bought with
  // picks for a piece in the bag (p.boughtWith) first, then the ones most shoppers can buy (most sizes in stock), then
  // cheapest. Sorting by price alone led with Eclipse pants, which only come in S.
  function suggestions(limit = 3) {
    const p = price(lines);
    const inBag = new Set(lines.map(l => l.handle));
    const fav = new Set(lines.flatMap(l => (byHandle(l.handle) || {}).boughtWith || []));
    const open = x => x.sizes.filter(s => s.available).length;
    return p.missingCategories
      .flatMap(c => inCategory(c).filter(x => !inBag.has(x.handle) && open(x)).sort((a, b) => fav.has(b.handle) - fav.has(a.handle) || open(b) - open(a) || a.price - b.price).slice(0, 2))
      .slice(0, limit);
  }

  const api = { DATA, products, byHandle, siblings, inCategory, money, stockNote, sizeForWeight, price, setCode, cart, suggestions };
  if (typeof module !== 'undefined' && module.exports) module.exports = api; else root.Store = api;
})(typeof window !== 'undefined' ? window : globalThis);
